// AdminFeedback.tsx — /admin/feedback (Task 4 of the feedback-form cycle, spec
// docs/superpowers/specs/2026-08-18-feedback-form-design.md "Dashboard — admin side"). Mirrors
// AdminJobs.tsx's tab/table/per-row-mutation idiom, but the filter is a plain two-state
// `handled=all|false` (the API's `AdminFeedbackListDto` carries no per-tab `counts` the way
// `AdminJobsFilteredDto` does, so — unlike Jobs — these tabs show no counts; the unhandled
// COUNT lives only on the nav tab's chip, see AdminLayout.tsx). Reply is deliberately a
// `mailto:` link, not an in-admin composer (spec: "Reply channel: mailbox only") — the id
// fragment in the subject line (`id.slice(0, 8)`) is what lets mailbox search and the
// `feedback_notify` email's body-embedded id line up (see email-templates.ts).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminFeedbackDto, AdminFeedbackListDto, FeedbackType } from '@repo/shared-types';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import { SkeletonText } from '@repo/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/table';
import { AdminPagination } from '../components/AdminPagination';
import { apiFetch, ApiError } from '../lib/api';
import { relativeTime } from '../lib/relative-time';
import { useT } from '../locales/context';
import type { Catalog } from '../locales/en';

type FeedbackFilter = 'unhandled' | 'all';

const FILTERS: FeedbackFilter[] = ['unhandled', 'all'];
/** Wire value each tab sends as `?handled=` — the API only recognizes `all|false` (spec: "a
 * single handled boolean, no pipeline" — there's deliberately no `?handled=true` tab). */
const FILTER_TO_HANDLED: Record<FeedbackFilter, 'all' | 'false'> = {
  unhandled: 'false',
  all: 'all',
};

const TYPE_VARIANT: Record<FeedbackType, 'destructive' | 'default' | 'secondary'> = {
  bug: 'destructive',
  feature: 'default',
  question: 'secondary',
};

function queryKey(filter: FeedbackFilter, page: number) {
  return ['admin', 'feedback', filter, page] as const;
}

function mailtoHref(row: AdminFeedbackDto, t: Catalog): string {
  const subject = t.admin.feedback.mailtoSubject({ id: row.id.slice(0, 8), type: row.type });
  // RFC 6068: the address segment is a URI component too — a stored local-part containing
  // `?`/`&`/`#` (valid per the upstream email regex) would otherwise corrupt the query
  // boundary. Snapshot emails can't be re-validated away, so encode both halves.
  return `mailto:${encodeURIComponent(row.userEmail)}?subject=${encodeURIComponent(subject)}`;
}

/** Table shape at the real 44px row height (handoff Shell Anatomy §06). */
function TableSkeleton() {
  const widths = ['w-[85%]', 'w-[65%]', 'w-[70%]', 'w-[90%]', 'w-[50%]'];
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border">
      <div className="h-[34px] bg-muted" />
      {widths.map((w, i) => (
        <div key={w} className={`flex h-11 items-center px-4 ${i === 0 ? '' : 'border-t'}`}>
          <SkeletonText className={w} />
        </div>
      ))}
    </div>
  );
}

export function AdminFeedback() {
  const t = useT();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<FeedbackFilter>('unhandled');
  const [page, setPage] = useState(1);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  const { data, isPending, error } = useQuery({
    queryKey: queryKey(filter, page),
    queryFn: () =>
      apiFetch<AdminFeedbackListDto>(
        `/api/admin/feedback?handled=${FILTER_TO_HANDLED[filter]}&page=${page}`,
      ),
  });

  function selectTab(next: FeedbackFilter) {
    setFilter(next);
    setPage(1);
  }

  const toggleHandled = useMutation({
    mutationFn: ({ id, handled }: { id: string; handled: boolean }) =>
      apiFetch<AdminFeedbackDto>(`/api/admin/feedback/${id}/handled`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handled }),
      }),
    onSuccess: (_data, { id }) => {
      setRowErrors((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] });
    },
    onError: (err, { id }) =>
      setRowErrors((prev) => ({
        ...prev,
        [id]: err instanceof ApiError ? err.message : t.admin.feedback.updateFeedbackErrorFallback,
      })),
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="no-scrollbar overflow-x-auto border-b">
        <nav className="flex gap-1 whitespace-nowrap">
          {FILTERS.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => selectTab(tab)}
              className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                filter === tab
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.admin.feedback.filterLabel[tab]}
            </button>
          ))}
        </nav>
      </div>

      {isPending && <TableSkeleton />}
      {error && <p className="text-destructive">{error.message}</p>}

      {data && data.items.length === 0 && (
        <p className="text-muted-foreground">
          {filter === 'unhandled' ? t.admin.feedback.emptyUnhandled : t.admin.feedback.emptyAll}
        </p>
      )}

      {data && data.items.length > 0 && (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.admin.feedback.colDate}</TableHead>
                <TableHead>{t.admin.feedback.colWorkspace}</TableHead>
                <TableHead>{t.admin.feedback.colUser}</TableHead>
                <TableHead>{t.admin.feedback.colType}</TableHead>
                <TableHead>{t.admin.feedback.colMessage}</TableHead>
                <TableHead>{t.admin.feedback.colActions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((row) => {
                const pending = toggleHandled.isPending && toggleHandled.variables?.id === row.id;
                return (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-nowrap">
                      {relativeTime(row.createdAt, t.time)}
                    </TableCell>
                    <TableCell>{row.workspaceName}</TableCell>
                    <TableCell>{row.userEmail}</TableCell>
                    <TableCell>
                      <Badge variant={TYPE_VARIANT[row.type]}>{t.admin.feedback.typeLabel[row.type]}</Badge>
                    </TableCell>
                    <TableCell className="max-w-md min-w-64">
                      <p className="break-words whitespace-pre-wrap">{row.message}</p>
                      {row.context && (
                        <p
                          className="mt-1 truncate text-xs text-muted-foreground"
                          title={row.context.userAgent ?? undefined}
                        >
                          {row.context.path ?? '—'} · {row.context.botId ?? t.admin.feedback.noBotFallback} ·{' '}
                          {row.context.userAgent
                            ? `${row.context.userAgent.slice(0, 40)}…`
                            : t.admin.feedback.noUaFallback}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1">
                        <a href={mailtoHref(row, t)} className="text-sm text-primary hover:underline">
                          {t.admin.feedback.replyCta}
                        </a>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={pending}
                          onClick={() =>
                            toggleHandled.mutate({ id: row.id, handled: !row.handled })
                          }
                        >
                          {pending
                            ? row.handled
                              ? t.admin.feedback.reopeningCta
                              : t.admin.feedback.markingCta
                            : row.handled
                              ? t.admin.feedback.reopenCta
                              : t.admin.feedback.markHandledCta}
                        </Button>
                        {rowErrors[row.id] && (
                          <span className="text-xs text-destructive">{rowErrors[row.id]}</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {data && <AdminPagination meta={data} onPageChange={setPage} />}
    </div>
  );
}
